import { Building2, ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { buildDepartmentTree, type DepartmentTreeNode } from '../lib/departmentTree'
import type { Department } from '../types'

type Props = {
  departments: Department[]
  selectedId: string | null
  onSelect: (department: Department) => void
  className?: string
}

export function DepartmentTree({ departments, selectedId, onSelect, className }: Props) {
  const { t } = useTranslation()
  const roots = useMemo(() => buildDepartmentTree(departments), [departments])
  const [open, setOpen] = useState<Set<string>>(() => new Set(roots.map((r) => r.id)))

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  if (roots.length === 0) {
    return <p className="px-3 py-6 text-center text-xs text-muted-foreground">{t('access.departments.tree.empty')}</p>
  }

  return (
    <div className={cn('space-y-0.5', className)}>
      <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('access.departments.tree.title')}</p>
      {roots.map((node) => (
        <TreeNode key={node.id} node={node} depth={0} open={open} selectedId={selectedId} onToggle={toggle} onSelect={onSelect} />
      ))}
    </div>
  )
}

function TreeNode({
  node,
  depth,
  open,
  selectedId,
  onToggle,
  onSelect,
}: {
  node: DepartmentTreeNode
  depth: number
  open: Set<string>
  selectedId: string | null
  onToggle: (id: string) => void
  onSelect: (department: Department) => void
}) {
  const hasChildren = node.children.length > 0
  const expanded = open.has(node.id)
  const selected = selectedId === node.id

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1 rounded-lg px-1.5 py-1 text-sm transition-colors',
          selected ? 'bg-brand-blue/10 text-foreground' : 'hover:bg-muted/70 text-muted-foreground hover:text-foreground',
        )}
        style={{ paddingLeft: 6 + depth * 14 }}
      >
        {hasChildren ? (
          <button type="button" className="flex size-6 shrink-0 items-center justify-center rounded-md hover:bg-muted" aria-expanded={expanded} onClick={() => onToggle(node.id)}>
            {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          </button>
        ) : (
          <span className="size-6 shrink-0" />
        )}
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-0.5 text-left" onClick={() => onSelect(node)}>
          <Building2 className={cn('size-3.5 shrink-0', selected ? 'text-brand-blue' : '')} />
          <span className="truncate font-medium">{node.name}</span>
          <span className="ml-auto shrink-0 font-mono text-[10px] text-muted-foreground">{node.code}</span>
        </button>
      </div>
      {hasChildren && expanded
        ? node.children.map((child) => (
            <TreeNode key={child.id} node={child} depth={depth + 1} open={open} selectedId={selectedId} onToggle={onToggle} onSelect={onSelect} />
          ))
        : null}
    </div>
  )
}
